import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, Check } from 'lucide-react';
import { cn } from '../lib/utils';
import { changeLanguage, getCurrentLanguage } from '../lib/i18n';

interface LanguageSwitcherProps {
  variant?: 'button' | 'menu' | 'inline';
  showLabel?: boolean;
}

const LANGUAGES = [
  { code: 'en', label: 'English', nativeLabel: 'English', flag: '🇺🇸' },
  { code: 'ar', label: 'Arabic', nativeLabel: 'العربية', flag: '🇸🇦' },
] as const;

export default function LanguageSwitcher({ variant = 'button', showLabel = true }: LanguageSwitcherProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const currentLang = getCurrentLanguage();

  const handleChange = (lang: 'en' | 'ar') => {
    changeLanguage(lang);
    setIsOpen(false);
  };

  // Simple toggle button
  if (variant === 'inline') {
    return (
      <button
        onClick={() => handleChange(currentLang === 'en' ? 'ar' : 'en')}
        className="btn-ghost gap-1.5"
        aria-label={t('settings.language')}
      >
        <Globe className="h-4 w-4" />
        {showLabel && (
          <span className="text-sm font-medium">
            {currentLang === 'en' ? 'ع' : 'EN'}
          </span>
        )}
      </button>
    );
  }

  // Dropdown button
  if (variant === 'button') {
    return (
      <div className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={cn('btn-ghost gap-1.5', isOpen && 'bg-secondary')}
          aria-label={t('settings.language')}
        >
          <Globe className="h-4 w-4" />
          {showLabel && (
            <span className="text-sm">
              {LANGUAGES.find(l => l.code === currentLang)?.nativeLabel}
            </span>
          )}
        </button>

        {isOpen && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
            <div className="dropdown-content absolute end-0 top-full mt-1 w-40 z-50">
              {LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => handleChange(lang.code)}
                  className={cn(
                    'menu-item justify-between',
                    currentLang === lang.code && 'bg-secondary'
                  )}
                >
                  <span className="flex items-center gap-2">
                    <span>{lang.flag}</span>
                    <span>{lang.nativeLabel}</span>
                  </span>
                  {currentLang === lang.code && (
                    <Check className="h-4 w-4 text-primary" />
                  )}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    );
  }

  // Full menu variant
  return (
    <div className="space-y-2">
      <label className="text-sm font-medium">
        {t('settings.language')}
      </label>
      <div className="flex flex-wrap gap-2">
        {LANGUAGES.map((lang) => (
          <button
            key={lang.code}
            onClick={() => handleChange(lang.code)}
            className={cn(
              'flex items-center gap-2 rounded-lg border px-4 py-2.5 transition-all',
              currentLang === lang.code
                ? 'border-primary bg-primary/10 text-foreground'
                : 'border-border hover:border-primary/50 hover:bg-secondary'
            )}
          >
            <span>{lang.flag}</span>
            <span className="text-sm">{lang.nativeLabel}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
